import { HTMLAttributes, useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  GripVertical,
  MessageSquareText,
  MoreHorizontal,
  Trash2,
  Type,
} from "lucide-react";

import { Input } from "@/app/admin/_common/ui/input";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import { FIELD_TYPES, ChecklistFieldType } from "@/app/_services/ChecklistService";
import { cn } from "@/app/_utils/cn";
import { FIELD_TYPE_ICONS } from "@/app/admin/_common/checklistIcons";

import type { DraftField } from "../checklistDraft";
import type { DropPosition } from "../useDragReorder";
import { FIELD_GRID } from "./fieldGrid";
import { HIGHLIGHT_CLASSES } from "./highlight";
import SelectOptionsInput from "./SelectOptionsInput";

// Borderless until hovered or focused, so rows read as text rather than a wall of inputs.
const inlineInput =
  "border-transparent bg-transparent px-1.5 shadow-none hover:border-input focus-visible:border-input";

const iconButton =
  "flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-30";

export default function FieldRow({
  field,
  index,
  count,
  onChange,
  onMove,
  onRemove,
  dragHandlers,
  dropPosition,
  isDragging,
  highlightNonce,
}: {
  field: DraftField;
  index: number;
  count: number;
  onChange: (patch: Partial<DraftField>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  dragHandlers: HTMLAttributes<HTMLElement>;
  dropPosition: DropPosition | null;
  isDragging: boolean;
  highlightNonce: number | null;
}) {
  // Only the grip starts a drag; a draggable row would swallow text selection in its inputs.
  const [armed, setArmed] = useState(false);
  const Icon = FIELD_TYPE_ICONS[field.type] ?? Type;

  // Help text only shows once it has content, or while being added from the menu.
  const [addingHelp, setAddingHelp] = useState(false);
  const helpRef = useRef<HTMLInputElement>(null);
  const showHelp = field.help !== "" || addingHelp;

  useEffect(() => {
    if (addingHelp) helpRef.current?.focus();
  }, [addingHelp]);

  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlightNonce !== null) rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightNonce]);

  // Collapsed by default; a select with no options yet opens so they can be added.
  const [optionsOpen, setOptionsOpen] = useState(field.options.length === 0);
  const filledOptions = field.options.filter((o) => o.trim());
  const optionCount = filledOptions.length;

  return (
    <div
      ref={rowRef}
      draggable={armed}
      {...dragHandlers}
      onDragEnd={(e) => {
        setArmed(false);
        dragHandlers.onDragEnd?.(e);
      }}
      className={cn(
        "relative flex flex-col gap-2 border-b border-border py-2 pl-2 pr-4 last:border-b-0",
        FIELD_GRID,
        "transition-colors",
        isDragging && "opacity-40",
        highlightNonce !== null && HIGHLIGHT_CLASSES
      )}
    >
      {dropPosition && (
        <span
          className={cn(
            "pointer-events-none absolute inset-x-0 h-0.5 bg-primary",
            dropPosition === "before" ? "-top-px" : "-bottom-px"
          )}
        />
      )}

      <div className="flex min-w-0 items-start gap-1">
        <button
          type="button"
          onMouseDown={() => setArmed(true)}
          onMouseUp={() => setArmed(false)}
          className="hidden h-[30px] w-6 cursor-grab items-center justify-center rounded text-muted-foreground/60 hover:text-muted-foreground active:cursor-grabbing md:flex"
          aria-label={`Drag ${field.label || "field"} to reorder`}
        >
          <GripVertical className="size-5" />
        </button>
        <div className="mr-1 flex size-[30px] flex-none items-center justify-center rounded-md bg-muted text-foreground">
          <Icon className="size-4" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <Input
            value={field.label}
            placeholder="Field label"
            onChange={(e) => onChange({ label: e.target.value })}
            className={cn(inlineInput, "text-[13px]")}
          />
          {showHelp && (
            <Input
              ref={helpRef}
              value={field.help}
              placeholder="Add help text…"
              onChange={(e) => onChange({ help: e.target.value })}
              onBlur={() => setAddingHelp(false)}
              className={cn(inlineInput, "text-[12px] text-muted-foreground")}
            />
          )}
          {field.type === "select" && (
            <div className="flex flex-col px-1.5">
              <button
                type="button"
                onClick={() => setOptionsOpen((open) => !open)}
                className="flex h-[30px] w-fit min-w-0 max-w-full items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground"
                aria-expanded={optionsOpen}
              >
                <ChevronRight
                  className={cn("size-4 flex-none transition-transform", optionsOpen && "rotate-90")}
                />
                <span className="truncate">
                  {optionCount} {optionCount === 1 ? "option" : "options"}
                  {!optionsOpen && optionCount > 0 && `: ${filledOptions.join(", ")}`}
                </span>
              </button>
              {optionsOpen && (
                <div className="pb-1 pl-5">
                  <SelectOptionsInput
                    options={field.options}
                    onChange={(options) => onChange({ options })}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Select
        value={field.type}
        onValueChange={(type) => onChange({ type: type as ChecklistFieldType })}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FIELD_TYPES.map((t) => (
            <SelectItem key={t.value} value={t.value}>
              {t.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex h-[30px] items-center md:justify-center">
        <Checkbox
          checked={field.required}
          onCheckedChange={(checked) => onChange({ required: checked === true })}
          aria-label={`${field.label || "Field"} is required`}
        />
      </div>

      <div className="flex h-[30px] items-center md:justify-center">
        <button
          type="button"
          className={iconButton}
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label={`Move ${field.label || "field"} up`}
        >
          <ChevronUp className="size-4" />
        </button>
        <button
          type="button"
          className={iconButton}
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          aria-label={`Move ${field.label || "field"} down`}
        >
          <ChevronDown className="size-4" />
        </button>
      </div>

      <div className="flex h-[30px] items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={iconButton}
              aria-label={`More actions for ${field.label || "field"}`}
            >
              <MoreHorizontal className="size-4" />
            </button>
          </DropdownMenuTrigger>
          {/* Skip refocusing the trigger on close, or it would blur the help input we just opened. */}
          <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
            {field.help === "" ? (
              <DropdownMenuItem onClick={() => setAddingHelp(true)}>
                <MessageSquareText className="mr-2 size-4" />
                Add help text
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onChange({ help: "" })}>
                <MessageSquareText className="mr-2 size-4" />
                Remove help text
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              className="text-red-600 focus:bg-red-50 focus:text-red-700"
              onClick={onRemove}
            >
              <Trash2 className="mr-2 size-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
